/**
 * DiagnosticRegistry.js
 * Central registry for DataTableWrapper diagnostic plugins.
 */
export class DiagnosticRegistry {
    constructor() {
        /** @type {Map<string, DiagnosticPlugin>} */
        this._plugins = new Map();
    }

    /**
     * Registers a diagnostic plugin.
     * @param {DiagnosticPlugin} plugin
     * @returns {DiagnosticRegistry}
     */
    register(plugin) {
        if (!plugin || typeof plugin !== 'object') {
            throw new Error('DiagnosticRegistry: Plugin must be an object.');
        }
        if (!plugin.id || typeof plugin.id !== 'string') {
            throw new Error('DiagnosticRegistry: Plugin must have a valid string `id`.');
        }
        if (typeof plugin.run !== 'function') {
            throw new Error(`DiagnosticRegistry: Plugin "${plugin.id}" must implement a \`run(context)\` function.`);
        }

        this._plugins.set(plugin.id, {
            id: plugin.id,
            name: plugin.name || plugin.id,
            category: plugin.category || 'general',
            description: plugin.description || '',
            run: plugin.run
        });
        return this;
    }

    /**
     * Unregisters a plugin by ID.
     * @param {string} id
     * @returns {boolean}
     */
    unregister(id) {
        return this._plugins.delete(id);
    }

    /**
     * Retrieves a plugin by ID.
     * @param {string} id
     * @returns {DiagnosticPlugin|undefined}
     */
    get(id) {
        return this._plugins.get(id);
    }

    /**
     * Returns all registered plugins.
     * @returns {DiagnosticPlugin[]}
     */
    getAll() {
        return Array.from(this._plugins.values());
    }

    /**
     * Clears all registered plugins.
     */
    clear() {
        this._plugins.clear();
    }
}

export const defaultDiagnosticRegistry = new DiagnosticRegistry();
